import { css } from "@emotion/react"
import { Typography } from "matsuri-ui"
import dayjs from "dayjs"
import { useMemo } from "react"
import { theme } from "@/theme"
import { useTranslation } from "@/hooks/useTranslation"

const deadlineStyle = css`
    display: block;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    color: #ffb74d;
    text-align: left;
`

interface ApprovalRequestCardProps {
    color?: string
    listingName: string
    tourName: string
    schedule: string
    deadlineAt?: number | null
    hasCheckinOnDate?: boolean
    cleaningPrice: React.ReactNode
    href: string
}

const ApprovalRequestCard = ({
    color = "inherit",
    listingName,
    tourName,
    schedule,
    deadlineAt,
    hasCheckinOnDate,
    cleaningPrice,
    href,
    ...props
}: ApprovalRequestCardProps) => {
    const { t } = useTranslation()

    const deadlineText = useMemo(() => {
        if (deadlineAt) {
            return t("終了期限 〜{{time}}", {
                params: {
                    time: dayjs.unix(deadlineAt).format("HH:mm")
                }
            })
        }
        if (hasCheckinOnDate) {
            return t("終了期限 〜16:00")
        }
        return null
    }, [deadlineAt, hasCheckinOnDate])

    return (
        <a
            css={css`
                position: relative;
                min-height: 148px;
                padding: 16px;
                color: inherit;
                background-color: white;
                border-radius: 10px;
                box-shadow:
                    0px 4px 6px -1px rgba(0, 0, 0, 0.1),
                    0px 2px 4px -2px rgba(0, 0, 0, 0.1);
                &:active {
                    background-color: ${theme.palette.gray[200]};
                }
            `}
            {...props}
            href={href}
        >
            <div
                css={css`
                    position: absolute;
                    top: -8px;
                    left: -8px;
                    width: 16px;
                    height: 16px;
                    background-color: #908062;
                    border-radius: 50%;
                    box-shadow:
                        0px 10px 15px -3px rgba(0, 0, 0, 0.1),
                        0px 4px 6px -4px rgba(0, 0, 0, 0.1);
                `}
            />
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    justify-content: space-between;
                    height: 100%;
                `}
            >
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                    `}
                >
                    <Typography
                        bolder
                        clamp={2}
                        css={css`
                            min-height: 42px;
                            font-size: 14px;
                            text-align: left;
                            word-break: break-all; /* 単語を任意の位置で折り返す */
                            word-wrap: break-word; /* 長い単語でも折り返しを許可 */
                        `}
                    >
                        {listingName}
                    </Typography>
                    <Typography
                        clamp={2}
                        color="primary"
                        css={css`
                            font-size: 14px;
                            line-height: 24px;
                            text-align: left;
                        `}
                    >
                        {tourName}
                    </Typography>
                    <span
                        css={css`
                            display: block;
                            font-size: 12px;
                            line-height: 16px;
                            text-align: left;
                        `}
                    >
                        {t(schedule)}
                    </span>
                    {deadlineText && (
                        <span css={deadlineStyle}>{deadlineText}</span>
                    )}
                </div>
                <div
                    css={css`
                        display: flex;
                        justify-content: flex-end;
                    `}
                >
                    <Typography variant="h4" bolder>
                        {cleaningPrice}
                    </Typography>
                </div>
            </div>
        </a>
    )
}

export default ApprovalRequestCard
